import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from "react-helmet";
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Leaf, Droplets, FlaskConical, Factory, 
  Recycle, Award, Users, Target, Cpu, TrendingUp, CircleFadingArrowUp, Grip, Activity, ChevronLeft, ChevronRight
} from 'lucide-react';

import renvitek_intro from './images1/renvitek_intro.png';
import rbhm from './images1/RBHMT.png';
import owc from './images1/OWC.png';
import logo_L from './images1/logo_L.png';
import cbg from './images1/biogas.png';
const LOGO_URL = logo_L;

// Hero carousel slides
const heroSlides = [
  {
    id: 1,
    tagline: 'Engineering the Sustainable Future',
    title: 'Welcome to RENVITEK Engineers LLP',
    subtitle: 'Committed to engineering a sustainable future through research-driven Bio-CNG, solid waste, and wastewater solutions.',
    image: renvitek_intro,
    cta: { text: 'Get Started', link: '/contact' }
  },
  {
    id: 2,
    tagline: 'Waste to Energy Solutions',
    title: 'Commercial Biogas & CBG Plants',
    subtitle: 'Full-scale facilities for continuous production of Raw biogas and CBG for long-term revenue generation with 98% methane purity.',
    image: cbg,
    cta: { text: 'Explore Services', link: '/services/biogas' }
  },
  {
    id: 3,
    tagline: 'Advanced Technology',
    title: 'RBHM™ Technology',
    subtitle: 'Proprietary biodigester health intelligence and decision-support framework for stable, efficient, and long-term operation.',
    image: rbhm,
    cta: { text: 'Learn More', link: '/services/rbhm' }
  },
  {
    id: 4,
    tagline: 'Water Treatment Excellence',
    title: 'Integrated Water Solutions',
    subtitle: 'End-to-end water and wastewater treatment including STP, ETP, RO, ZLD, MEE, and ATFD systems.',
    image: 'https://images.unsplash.com/photo-1765802324460-554ae5a65108?w=1920&q=80',
    cta: { text: 'View Solutions', link: '/services/water' }
  },
  {
    id: 5,
    tagline: 'Sustainable Composting',
    title: 'Organic Waste Management',
    subtitle: 'BIOGUEST-V, OWC, and home composting solutions aligned with circular economy principles.',
    image: owc,
    cta: { text: 'Discover More', link: '/services/composting' }
  }
];

const services = [
  {
    icon: Factory,
    title: 'Commercial Biogas-CBG',
    description: 'Full-scale facilities for continuous production of Raw biogas and CBG for long-term revenue generation.',
    link: '/services/biogas'
  },
  {
    icon: Target,
    title: 'RBHM Technology',
    description: 'Proprietary biodigester health intelligence and decision-support framework for stable, efficient operation.',
    link: '/services/rbhm'
  },
  {
    icon: Recycle,
    title: 'RRR Prototype Digester™',
    description: 'Next-generation anaerobic digestion for decentralized organic waste treatment with biogas recovery.',
    link: '/services/RRRProto'
  },
  {
    icon: FlaskConical,
    title: 'Biogas Lab Services',
    description: 'Comprehensive lab services including BMP testing, SMA analysis, and 35+ analytical parameters.',
    link: '/services/lab'
  },
  {
    icon: Droplets,
    title: 'Water & Wastewater',
    description: 'End-to-end water treatment solutions including STP, ETP, RO, ZLD, MEE, and ATFD systems.',
    link: '/services/water'
  },
  {
    icon: CircleFadingArrowUp,
    title: ' Digestate Management & Manure Upgradation',
    description: 'Digestate is the nutrient-rich residual material generated from the anaerobic digestion (AD) process.',
    link: '/services/dige'
  },
  {
    icon: Leaf,
    title: 'Composting Systems',
    description: 'BIOGUEST-V, OWC, and home composting solutions for sustainable organic waste management.',
    link: '/services/composting'
  },
  {
    icon: Grip,
    title: 'Biomass Briquettes & Pellets ',
    description: 'Biomass briquettes and pellets are produced from renewable organic residues such as agricultural by-products, forestry waste, and wood-based biomass.',
    link: '/services/biomass'
  }
];

const stats = [
  { value: '15+', label: 'Years Experience' },
  { value: '100+', label: 'Projects Completed' },
  { value: '35+', label: 'Lab Parameters' },
  { value: '98%', label: 'Methane Purity' }
];

const testimonials = [
  {
    quote: "Srinivas Jukuri, Suresh Bastipati, Bhagawan Dheravath & Saida Lavudi(2021). Biochemical process evaluation of an anaerobic digester: Case studies on long sustain commercial biogas plant. Biomass Conversion and Biorefinery.",
    link: "https://doi.org/10.1007/s13399-021-01410-3",
    author: "Industrial Client",
    company: "CBG Plant Operator"
  },
  {
    quote: "The team's expertise in anaerobic digestion and their data-driven approach helped us achieve optimal gas yields consistently.",
    author: "Project Partner",
    company: "Waste-to-Energy Facility"
  },
  {
    quote: "Professional, knowledgeable, and always available. RENVITEK has been a trusted partner in our sustainability journey.",
    author: "Institutional Client",
    company: "Educational Institution"
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

export const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);

  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setDirection(-1);
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
      scale: 1.1
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.9
    })
  };

  const textVariants = {
    enter: { opacity: 0, y: 50 },
    center: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -50 }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Carousel Section */}
     <section className="relative mt-20 h-screen overflow-hidden hidden md:block">
        {/* Background Images */}
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={currentSlide}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.5 },
              scale: { duration: 0.5 }
            }}
            className="absolute inset-0"
          >
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-top"
              style={{ backgroundImage: `url(${heroSlides[currentSlide].image})` }}
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/95 via-emerald-950/30 to-emerald-950/60" />
          </motion.div>
        </AnimatePresence>

        {/* Animated Pattern Overlay */}
        <div className="absolute inset-0 z-[1] opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2310b981' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        {/* Content */}
        <div className="relative z-[2] h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Text Content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  variants={{
                    enter: { opacity: 0 },
                    center: { opacity: 1 },
                    exit: { opacity: 0 }
                  }}
                  transition={{ duration: 0.5 }}
                  className="max-w-2xl"
                >
                  <motion.span 
                    className="inline-block text-amber-400 uppercase tracking-[0.3em] text-sm font-semibold mb-6"
                    variants={textVariants}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    {heroSlides[currentSlide].tagline}
                  </motion.span>
                  
                  <motion.h1 
                    className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 leading-tight"
                    variants={textVariants}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    {heroSlides[currentSlide].title.includes('RENVITEK') ? (
                      <>
                        {heroSlides[currentSlide].title.split('RENVITEK')[0]}
                        <span className="text-gradient-gold font-accent">RENVITEK</span>
                        {heroSlides[currentSlide].title.split('RENVITEK')[1]}
                      </>
                    ) : (
                      heroSlides[currentSlide].title
                    )}
                  </motion.h1>
                  
                  <motion.p 
                    className="text-lg md:text-xl text-white/70 mb-10 leading-relaxed"
                    variants={textVariants}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    {heroSlides[currentSlide].subtitle}
                  </motion.p>
                  
                  <motion.div 
                    className="flex flex-wrap gap-4"
                    variants={textVariants}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    <Link to={heroSlides[currentSlide].cta.link}>
                      <motion.button
                        className="bg-amber-500 hover:bg-amber-400 text-emerald-950 uppercase tracking-widest px-8 py-4 font-semibold flex items-center gap-2 transition-all duration-300"
                        whileHover={{ scale: 1.02, x: 5 }}
                        whileTap={{ scale: 0.98 }}
                        data-testid="hero-cta-primary"
                      >
                        {heroSlides[currentSlide].cta.text}
                        <ArrowRight size={20} />
                      </motion.button>
                    </Link>
                    <Link to="/services">
                      <motion.button
                        className="bg-emerald-950 border-2 border-emerald-600 text-white hover:bg-emerald-500 uppercase tracking-widest px-8 py-4 font-semibold transition-all duration-300"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        data-testid="hero-cta-secondary"
                      >
                        Our Services
                      </motion.button>
                    </Link>
                  </motion.div>
                </motion.div>
              </AnimatePresence>

            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        {/* <div className="absolute bottom-1/2 translate-y-1/2 left-4 md:left-8 z-[3]">
          <motion.button
            onClick={prevSlide}
            className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-all duration-300 border border-white/20"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            data-testid="carousel-prev"
          >
            <ChevronLeft size={24} />
          </motion.button>
        </div>
        <div className="absolute bottom-1/2 translate-y-1/2 right-4 md:right-8 z-[3]">
          <motion.button
            onClick={nextSlide}
            className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-all duration-300 border border-white/20"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            data-testid="carousel-next"
          >
            <ChevronRight size={24} />
          </motion.button>
        </div> */}

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-[3] flex items-center gap-3">
          {heroSlides.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => {
                setDirection(index > currentSlide ? 1 : -1);
                setCurrentSlide(index);
              }}
              className={`relative overflow-hidden transition-all duration-300 ${
                index === currentSlide 
                  ? 'w-12 h-3 bg-amber-500' 
                  : 'w-3 h-3 bg-white/30 hover:bg-white/50 rounded-full'
              }`}
              style={{ borderRadius: index === currentSlide ? '4px' : '50%' }}
              whileHover={{ scale: 1.2 }}
              data-testid={`slide-indicator-${index}`}
            >
              {index === currentSlide && (
                <motion.div
                  className="absolute inset-0 bg-amber-400"
                  initial={{ x: '-100%' }}
                  animate={{ x: '0%' }}
                  transition={{ duration: 6, ease: 'linear' }}
                />
              )}
            </motion.button>
          ))}
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-24 left-1/2 transform -translate-x-1/2 z-[3] hidden md:block"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-amber-400 rounded-full" />
          </div>
        </motion.div>

        {/* Slide Counter */}
        <div className="absolute bottom-8 right-8 z-[3] hidden md:flex items-center gap-2 text-white/50 font-mono text-sm">
          <span className="text-amber-400 text-lg font-bold">{String(currentSlide + 1).padStart(2, '0')}</span>
          <span>/</span>
          <span>{String(heroSlides.length).padStart(2, '0')}</span>
        </div>
      </section>
      {/* ================= MOBILE HERO ================= */}

        <section
        className="relative mt-20 min-h-[70vh] flex items-center justify-center md:hidden text-center px-6"
        style={{
          backgroundImage: `url(${LOGO_URL})`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center"
        }}
        >

        <div className="absolute inset-0 bg-emerald-950/80"></div>

        <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 max-w-xl"
        >

        <span className="text-amber-400 uppercase tracking-[0.3em] text-sm font-semibold block mb-4">
        Engineering the Sustainable Future
        </span>

        <h1 className="text-3xl font-bold text-white mb-6 leading-tight">
        Welcome to <span className="text-gradient-gold">RENVITEK</span> Engineers LLP
        </h1>

        <p className="text-white/70 text-base mb-8">
        Committed to engineering a sustainable future through research-driven
        Bio-CNG, solid waste, and wastewater solutions.
        </p>

        {/* Buttons */}
        <div className="flex flex-col gap-4">

        <Link to="/contact">
        <motion.button
        animate={{ y: [0, -8, 0] }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="bg-amber-500 hover:bg-amber-400 text-emerald-950 py-3 font-semibold w-full rounded-md shadow-lg"
        >
        Get Started
        </motion.button>
        </Link>

        <Link to="/services">
        <motion.button
        animate={{ y: [0, -8, 0] }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="border border-emerald-500 text-white py-3 w-full rounded-md hover:bg-emerald-600 hover:border-emerald-400 transition-all"
        >
        Our Services
        </motion.button>
        </Link>

        </div>

        </motion.div>

        </section>

      {/* Stats Section */}
      {/* <section className="py-16 bg-amber-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-4xl md:text-5xl font-bold text-emerald-950 mb-2">
                  {stat.value}
                </div>
                <div className="text-emerald-900 font-medium uppercase tracking-wider text-sm">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* About Preview */}
      <section className="py-12 md:py-16 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-amber-500 uppercase tracking-[0.2em] text-sm font-semibold">
                Welcome to RENVITEK Engineers LLP
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-emerald-900 dark:text-white mt-4 mb-6">
                Technology-Driven Solutions for Sustainable Energy
              </h2>
              <p className="text-slate-600 dark:text-white/70 mb-6 leading-relaxed tracking-wider">
                RENVITEK Engineers LLP, committed to engineering a sustainable future through research driven Bio-CNG, solid waste, and wastewater solutions. We specialize in converting agricultural residues and organic wastes into reliable, high-value renewable energy and environmentally compliant treatment systems.
              </p>
              <p className="text-slate-600 dark:text-white/70 mb-8 leading-relaxed tracking-wider">
                By integrating scientific rigor, technological innovation, and responsible engineering practices, we develop efficient, stable, and economically viable waste-to-energy solutions tailored to specific client requirements. Together, we turn waste challenges into resilient assets that support a cleaner, circular, and sustainable future.
              </p>
              <Link to="/about">
                <motion.button
                  className="btn-secondary dark:border-emerald-500 dark:text-emerald-400 dark:hover:bg-emerald-500 dark:hover:text-white flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  data-testid="about-cta"
                >
                  Learn More About Us
                  <ArrowRight size={18} />
                </motion.button>
              </Link>
            </motion.div>
            
            <motion.div
              className="grid grid-cols-2 gap-4"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {[
                { icon: Award, title: 'Domain Expertise', desc: 'Led by Ph.D.-level professionals with 15 + years of experience in the Biogas-CBG establishments & research.' },
                { icon: Target, title: 'Customized Solutions', desc: 'Every feedstock is different—we tailor systems to suit local resources and climatic conditions.' },
                { icon: Leaf, title: 'Sustainability First', desc: 'Our approach ensures long-term operability, minimal downtime, and maximum biogas/CBG yield.' },
                { icon: TrendingUp, title: 'Tech-Driven, Field-Tested', desc: 'From biological pre-hydrolysis to early warning indicators, we integrate the latest innovations to boost performance.' },
                { icon: Activity, title: 'Strong Local Roots', desc: 'Headquartered in Hyderabad, we have a deep understanding of India’s regulatory framework, climatic conditions, and industrial landscape.' },
                { icon: Cpu, title: 'Technical Collaborations with Esteemed Organizations', desc: 'We collaborate with JNTUH and ASCI–ES Laboratories to deliver comprehensive, high-quality services—spanning design, engineering, execution, process optimization, and performance monitoring.' }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="bg-emerald-50 dark:bg-emerald-900/50 p-6 hover:shadow-lg transition-shadow"
                  whileHover={{ y: -5 }}
                >
                  <item.icon className="w-10 h-10 text-emerald-600 dark:text-emerald-400 mb-4" />
                  <h4 className="font-semibold text-emerald-900 dark:text-white mb-2">
                    {item.title}
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-white/60 tracking-wider">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 md:py-16 bg-slate-50 dark:bg-emerald-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-amber-500 uppercase tracking-[0.2em] text-sm font-semibold">
              Our Services
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-emerald-900 dark:text-white mt-4">
              Core Competencies
            </h2>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {services.map((service) => (
              <motion.div
                key={service.title}
                variants={itemVariants}
                className="service-card bg-white dark:bg-emerald-900 p-8 shadow-sm"
              >
                <service.icon className="w-12 h-12 text-emerald-600 dark:text-emerald-400 mb-6" />
                <h3 className="text-xl font-semibold text-emerald-900 dark:text-white mb-4">
                  {service.title}
                </h3>
                <p className="text-slate-600 dark:text-white/60 mb-6 leading-relaxed tracking-wider">
                  {service.description}
                </p>
                <Link 
                  to={service.link}
                  className="inline-flex items-center gap-2 text-emerald-600 dark:text-amber-400 font-semibold hover:gap-3 transition-all"
                  data-testid={`service-link-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  Learn More <ArrowRight size={18} />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      

      {/* CTA Section */}
      <section className="py-12 md:py-10 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-emerald-950 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Ready to Transform Waste into Energy?
          </motion.h2>
          <motion.p
            className="text-emerald-900 text-lg mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Contact us today to discuss how RENVITEK can help you achieve your sustainability goals.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/contact">
              <motion.button
                className="bg-emerald-900 hover:bg-emerald-800 text-white px-10 py-4 uppercase tracking-wider font-semibold flex items-center gap-2 mx-auto"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="cta-contact-btn"
              >
                Contact Us Today
                <ArrowRight size={20} />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
